/**
 * Neue Ausgabe einer Richtlinie: Vorschau aus den Entwürfen (Einstufung je Abschnitt, endgültige
 * Nummern, alte und neue Fassung gegenübergestellt), Einreichen durch die Leitung des Arbeitskreises,
 * Freigabe oder Zurückweisung durch den Vorstand. Bei der Freigabe legt der Server die neue Ausgabe
 * als eigenes Dokument an; die Werkstatt springt dorthin.
 */

import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { ChevronDown, ChevronRight } from "lucide-react";

import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { cn } from "../lib/utils";
import { useGuidelinesAPI, type ChangeClass, type EditionPlanEntry, type EditionState } from "./GuidelinesAPI";
import { textDiff } from "./textDiff";

const CHANGE_CLASS: Record<ChangeClass, string> = {
  changed: "bg-amber-100 text-amber-900 dark:bg-amber-900/40 dark:text-amber-100",
  reworded: "bg-sky-100 text-sky-900 dark:bg-sky-900/40 dark:text-sky-100",
  new: "bg-emerald-100 text-emerald-900 dark:bg-emerald-900/40 dark:text-emerald-100",
  removed: "bg-rose-100 text-rose-900 dark:bg-rose-900/40 dark:text-rose-100",
  unchanged: "bg-muted text-muted-foreground",
};

const ORDER: ChangeClass[] = ["changed", "new", "removed", "reworded"];

/** Ausgabe des nächsten Monats als Vorschlag, z. B. „2026-11“. */
const nextMonth = () => {
  const d = new Date();
  d.setMonth(d.getMonth() + 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};

const errorText = (error: unknown) => (error instanceof Error ? error.message : String(error));

function EntryDiff({ entry }: { entry: EditionPlanEntry }) {
  const parts = useMemo(() => {
    if (entry.change === "new") return [{ kind: "added" as const, text: entry.text ?? "" }];
    if (entry.change === "removed") return [{ kind: "removed" as const, text: entry.oldText ?? "" }];
    return textDiff(entry.oldText ?? "", entry.text ?? "");
  }, [entry]);
  return (
    <div className="max-h-80 overflow-y-auto whitespace-pre-wrap rounded border bg-background p-3 text-sm leading-6">
      {parts.map((p, i) => (
        <span
          key={i}
          className={cn(
            p.kind === "added" && "bg-emerald-100 dark:bg-emerald-900/40",
            p.kind === "removed" && "bg-rose-100 text-muted-foreground line-through dark:bg-rose-900/40",
          )}
        >
          {p.text}
        </span>
      ))}
    </div>
  );
}

export function EditionDialog(props: {
  customer: string;
  documentId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Nach Einreichen, Zurückziehen, Zurückweisen neu laden; nach Freigabe zur neuen Ausgabe. */
  onChanged: (newDocument?: string) => void;
}) {
  const { t } = useTranslation();
  const api = useGuidelinesAPI(props.customer);
  const [state, setState] = useState<EditionState | null>(null);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [label, setLabel] = useState(nextMonth());
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!props.open) return;
    setState(null);
    api.getEdition(props.documentId).then(setState).catch((error) => toast.error(errorText(error)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.open, props.documentId]);

  const run = async (action: () => Promise<string | undefined | void>, success: string) => {
    setBusy(true);
    try {
      const newDocument = await action();
      toast.success(success);
      props.onOpenChange(false);
      props.onChanged(newDocument || undefined);
    } catch (error) {
      toast.error(errorText(error));
    } finally {
      setBusy(false);
    }
  };

  const affected = (state?.plan ?? [])
    .filter((e) => e.change !== "unchanged" || (e.oldNumber && e.newNumber && e.oldNumber !== e.newNumber))
    .sort((a, b) => ORDER.indexOf(a.change) - ORDER.indexOf(b.change));
  const s = state?.summary;
  const open = state?.open;
  const nothing = s && !s.changed && !s.reworded && !s.new && !s.removed;

  return (
    <Dialog open={props.open} onOpenChange={props.onOpenChange}>
      <DialogContent className="flex max-h-[90vh] max-w-4xl flex-col">
        <DialogHeader>
          <DialogTitle>{open ? t("guidelinesPage.edition.titleOpen", { label: open.label }) : t("guidelinesPage.edition.title")}</DialogTitle>
          <DialogDescription>
            {open
              ? t("guidelinesPage.edition.openSince", { date: new Date(open.submittedAt).toLocaleDateString("de-DE") })
              : t("guidelinesPage.edition.intro")}
          </DialogDescription>
        </DialogHeader>

        {!state ? (
          <p className="text-sm text-muted-foreground">{t("guidelinesPage.edition.loading")}</p>
        ) : (
          <>
            <div className="flex flex-wrap gap-2 text-xs">
              {(["changed", "reworded", "new", "removed"] as ChangeClass[]).map((c) => (
                <span key={c} className={cn("rounded px-2 py-1", CHANGE_CLASS[c])}>
                  {t(`guidelinesPage.edition.count.${c}`, { count: s![c as keyof typeof s] as number })}
                </span>
              ))}
              <span className="rounded bg-muted px-2 py-1 text-muted-foreground">{t("guidelinesPage.edition.count.renumbered", { count: s!.renumbered })}</span>
              <span className="rounded px-2 py-1 text-muted-foreground">{t("guidelinesPage.edition.count.unchanged", { count: s!.unchanged })}</span>
            </div>

            <div className="min-h-0 flex-1 overflow-y-auto rounded border">
              {nothing ? <p className="p-4 text-sm text-muted-foreground">{t("guidelinesPage.edition.nothing")}</p> : null}
              {affected.map((e) => {
                const isOpen = expanded.has(e.sectionId);
                const hasText = e.change !== "unchanged";
                return (
                  <div key={e.sectionId} className="border-b last:border-b-0">
                    <button
                      type="button"
                      disabled={!hasText}
                      onClick={() =>
                        setExpanded((prev) => {
                          const next = new Set(prev);
                          if (next.has(e.sectionId)) next.delete(e.sectionId);
                          else next.add(e.sectionId);
                          return next;
                        })
                      }
                      className="flex w-full items-center gap-3 px-3 py-2 text-left text-sm hover:bg-accent disabled:cursor-default disabled:hover:bg-transparent"
                    >
                      {hasText ? isOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} /> : <span className="w-[14px]" />}
                      <span className="w-36 shrink-0 tabular-nums text-muted-foreground">
                        {e.oldNumber ?? "–"} → {e.newNumber ?? t("guidelinesPage.edition.dropped")}
                      </span>
                      <span className="min-w-0 flex-1 truncate">{e.title}</span>
                      <span className={cn("shrink-0 rounded px-2 py-0.5 text-xs", CHANGE_CLASS[e.change])}>
                        {e.change === "unchanged" ? t("guidelinesPage.edition.class.renumbered") : t(`guidelinesPage.edition.class.${e.change}`)}
                      </span>
                    </button>
                    {isOpen ? (
                      <div className="px-3 pb-3">
                        <EntryDiff entry={e} />
                      </div>
                    ) : null}
                  </div>
                );
              })}
            </div>

            {state.history.filter((h) => h.status !== "submitted").length ? (
              <div className="text-xs text-muted-foreground">
                {state.history
                  .filter((h) => h.status !== "submitted")
                  .slice(0, 3)
                  .map((h) => (
                    <div key={h._id}>
                      {t(`guidelinesPage.edition.history.${h.status}`, { label: h.label, date: new Date(h.decidedAt ?? h.submittedAt).toLocaleDateString("de-DE") })}
                      {h.comment ? ` — „${h.comment}“` : ""}
                    </div>
                  ))}
              </div>
            ) : null}

            <div className="flex flex-wrap items-center justify-end gap-2 border-t pt-3">
              {open ? (
                <>
                  {state.permissions.approve ? (
                    <>
                      <Textarea
                        value={comment}
                        onChange={(e) => setComment(e.target.value)}
                        placeholder={t("guidelinesPage.edition.commentPlaceholder")}
                        className="min-h-[2.5rem] flex-1 text-sm"
                      />
                      <Button
                        variant="outline"
                        disabled={busy || !comment.trim()}
                        onClick={() => run(() => api.decideEdition(props.documentId, open._id, "reject", comment).then(() => undefined), t("guidelinesPage.edition.rejected"))}
                      >
                        {t("guidelinesPage.edition.reject")}
                      </Button>
                      <Button
                        disabled={busy}
                        onClick={() =>
                          run(
                            () => api.decideEdition(props.documentId, open._id, "approve", comment).then((r) => r.newDocument),
                            t("guidelinesPage.edition.approved", { label: open.label }),
                          )
                        }
                      >
                        {t("guidelinesPage.edition.approve", { label: open.label })}
                      </Button>
                    </>
                  ) : (
                    <span className="mr-auto text-sm text-muted-foreground">{t("guidelinesPage.edition.waitingForBoard")}</span>
                  )}
                  {state.permissions.reviewLinks ? (
                    <Button variant="ghost" disabled={busy} onClick={() => run(() => api.withdrawEdition(props.documentId, open._id).then(() => undefined), t("guidelinesPage.edition.withdrawn"))}>
                      {t("guidelinesPage.edition.withdraw")}
                    </Button>
                  ) : null}
                </>
              ) : state.permissions.reviewLinks ? (
                <>
                  <span className="text-sm text-muted-foreground">{t("guidelinesPage.edition.labelHint")}</span>
                  <Input value={label} onChange={(e) => setLabel(e.target.value)} className="h-9 w-28" aria-label={t("guidelinesPage.edition.label")} />
                  <Button
                    disabled={busy || !!nothing || !/^\d{4}-\d{2}$/.test(label)}
                    onClick={() => run(() => api.submitEdition(props.documentId, label).then(() => undefined), t("guidelinesPage.edition.submitted", { label }))}
                  >
                    {t("guidelinesPage.edition.submit")}
                  </Button>
                </>
              ) : (
                <span className="text-sm text-muted-foreground">{t("guidelinesPage.edition.leadOnly")}</span>
              )}
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

